// 纯 SVG 渲染到 Canvas：不使用 <foreignObject>，因此 drawImage 不污染 canvas。
// 这是 iOS Safari 客户端导出的基石——foreignObject 会 taint（Safari 不放宽 blob 例外），
// 而纯 SVG 元素（<text>/<rect>/<g>/<path>）经 blob URL → <img> → drawImage 在所有浏览器都不 taint。
//
// 与 browserExport.ts 的 foreignObject 路径并列：本模块负责「SVG → canvas 像素」，
// 编码（WebCodecs 帧推进 / MediaRecorder 实时录制）由各自模块负责。

export interface ExportProfile {
  width: number
  height: number
  bitrate: number
  fps: number
  maxDuration: number
  codec: string
  reason: string
}

export interface WatermarkOpts {
  text: string
  position?: string
  opacity?: number
  size?: number
}

// 所有场景 SVG 按 1080×1920 设计坐标，svgDoc 用 viewBox 缩放到 profile 分辨率（保持 9:16）。
export const DESIGN_W = 1080
export const DESIGN_H = 1920

// 与 SHARED_CSS :root 一致的设计色板，供场景 SVG 引用。
export const COLORS = {
  bg: '#08080c',
  bg1: '#0d0d13',
  bg2: '#13131a',
  bg3: '#1a1a23',
  border: 'rgba(255,255,255,0.06)',
  borderStrong: 'rgba(255,255,255,0.1)',
  p: '#6366f1',
  pHover: '#818cf8',
  pSubtle: 'rgba(99,102,241,0.12)',
  pGlow: 'rgba(99,102,241,0.35)',
  w: '#fafafa',
  t: '#e4e4e7',
  t2: '#a1a1aa',
  t3: '#71717a',
  t4: '#52525b',
} as const

export const FONT_SANS = "Inter,'Noto Sans SC',-apple-system,sans-serif"
export const FONT_MONO = "'JetBrains Mono',ui-monospace,monospace"

/**
 * 把场景内层 SVG（按 1080×1920 设计坐标）包成完整 SVG 文档。
 * viewBox 保持比例缩放到 profile 尺寸；不含 foreignObject → 不 taint。已铺底背景。
 */
export function svgDoc(inner: string, profile: ExportProfile, bgColor?: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${profile.width}" height="${profile.height}" viewBox="0 0 ${DESIGN_W} ${DESIGN_H}">
<rect x="0" y="0" width="${DESIGN_W}" height="${DESIGN_H}" fill="${bgColor || COLORS.bg}"/>
${inner}
</svg>`
}

/**
 * 纯 SVG 字符串 → Image → drawImage 到 canvas（不 taint）。
 * 调用方在 drawImage 之后再画水印（drawWatermark）。
 */
export function svgToCanvas(svg: string, ctx: CanvasRenderingContext2D, profile: ExportProfile): Promise<void> {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  return new Promise<void>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        ctx.drawImage(img, 0, 0, profile.width, profile.height)
        resolve()
      } catch (e) {
        reject(e)
      } finally {
        URL.revokeObjectURL(url)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('纯 SVG 渲染失败（drawImage）'))
    }
    img.src = url
  })
}

/** 文字水印（Canvas fillText，坐标相对 profile 实际分辨率）。与 browserExport 同名函数等价。 */
export function drawWatermark(ctx: CanvasRenderingContext2D, wm: WatermarkOpts, profile: ExportProfile): void {
  const W = profile.width
  const H = profile.height
  const opacity = wm.opacity ?? 0.6
  const size = wm.size ? wm.size : Math.round((48 * W) / DESIGN_W)
  ctx.font = `bold ${size}px ${FONT_SANS}`
  ctx.fillStyle = `rgba(255,255,255,${opacity})`
  const padding = Math.round((40 * W) / DESIGN_W)
  const tw = ctx.measureText(wm.text).width
  const th = size
  let x: number
  let y: number
  switch (wm.position) {
    case 'bottom-left':
      x = padding; y = H - th - padding; break
    case 'top-right':
      x = W - tw - padding; y = padding + th; break
    case 'top-left':
      x = padding; y = padding + th; break
    case 'center':
      x = (W - tw) / 2; y = (H + th) / 2; break
    default:
      x = W - tw - padding; y = H - th - padding
  }
  ctx.fillText(wm.text, x, y)
}

/** XML 转义（场景文本可能含 & < >）。 */
export function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** 近似文本换行：按字符估算宽度（CJK≈字宽，西文≈0.55），超 maxWidth 断行。降质量近似。 */
export function wrapText(text: string, fontSize: number, maxWidth: number): string[] {
  const lines: string[] = []
  let cur = ''
  let curW = 0
  for (const ch of String(text ?? '')) {
    const cjk = /[一-鿿　-〿＀-￯]/.test(ch)
    const w = cjk ? fontSize : fontSize * 0.55
    if (curW + w > maxWidth && cur) {
      lines.push(cur)
      cur = ch
      curW = w
    } else {
      cur += ch
      curW += w
    }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : ['']
}

/** 多行文本 → 一组 <text>（行高 1.3×fontSize，首行 y=yStart，middle baseline）。 */
export function textBlock(
  lines: string[],
  x: number,
  yStart: number,
  fontSize: number,
  fill: string,
  opts: { anchor?: 'start' | 'middle' | 'end'; font?: string; weight?: number; lineHeight?: number; style?: string } = {},
): string {
  const { anchor = 'start', font = FONT_SANS, weight = 400, lineHeight = 1.3, style } = opts
  const lh = fontSize * lineHeight
  const fontStyle = style ? ` font-style="${style}"` : ''
  return lines
    .map((ln, i) => {
      const t = esc(ln)
      return `  <text x="${x}" y="${yStart + i * lh}" font-size="${fontSize}" font-weight="${weight}" fill="${fill}" text-anchor="${anchor}" dominant-baseline="middle" font-family="${font}"${fontStyle}>${t}</text>`
    })
    .join('\n')
}
